<template>
  <accordion-item-fi
    v-if="hasContent"
    class="accordion-content-fi"
    header-tag="h3"
    ignore-others
    scroll-to
  >
    <template #header>
      {{ $dict.get('product-Catalog.Accordions.Content') }}
    </template>
    <template #content>
      <div class="accordion-content-fi__content">
        <div class="accordion-content-fi__column">
          <richtext-fi
            v-if="hasPackageContent"
            class="accordion-content-fi__text"
            :html-content="{ value: textListValue(textList, 'PACKAGE_CONTENT') }"
          />
          <richtext-fi
            class="accordion-content-fi__text"
            :html-content="{ value: attributeValue(attributes, 'INHALT') }"
          />
        </div>
        <div
          v-if="documents?.length"
          class="accordion-content-fi__links"
        >
          <link-fi
            v-for="document in documents"
            :key="document.Name"
            :href="document.Url"
            target="_blank"
            rel="noopener"
            class="accordion-content-fi__link"
            :size="18"
            primary
          >
            {{ document.CharacteristicValueNames?.[0] }}
          </link-fi>
        </div>
        <div
          v-if="hasPackageContentMedia"
          :class="{
            'accordion-content-fi__media': true,
            'accordion-content-fi__media--first':
              (!documents || (documents && !documents.length)) &&
              !attributeValue(attributes, 'INHALT') &&
              !hasPackageContent,
          }"
        >
          <div
            v-for="media in packageContentMedia"
            :key="media.Url"
            class="accordion-content-fi__media-item"
          >
            <cloud-image-fi
              :src="media.Url"
              :alt="media.label"
              :sizes="{
                xxs: { h: 115 },
                xs: { h: 115 },
                sm: { h: 125 },
                md: { h: 142 },
                lg: { h: 142 },
                xl: { h: 142 },
              }"
              params="func=bound"
              do-not-replace-url
            />
          </div>
        </div>
      </div>
    </template>
  </accordion-item-fi>
</template>

<script lang="ts" setup>
import { AccordionItemFi, CloudImageFi, LinkFi, RichtextFi } from 'atoms';
import { computed } from 'vue';

import { useCatalogAttributes } from '@/composables/catalogAttributes';
import { useCatalogTextList } from '@/composables/catalogTextList';
import { useFischer } from '@/composables/fischerPlugin';

interface CatalogAttribute {
  AttributeKey: string;
  AttributeName: string;
  Value: string;
  Unit: string;
}

interface Media {
  Type: string;
  Url: string;
  label?: string;
}

interface Document {
  Name: string;
  Url: string;
  Type: string;
  CharacteristicValueNames?: string[];
}

interface TextListItem {
  Key?: string;
  AttributeKey?: string;
  Value: string;
}

interface CatalogData {
  Attributes?: CatalogAttribute[];
  Media?: Media[];
  Documents?: {
    AdditionalDocuments?: Document[];
  };
  TextList?: TextListItem[];
}

interface Props {
  catalogData?: CatalogData;
}

const props = withDefaults(defineProps<Props>(), {
  catalogData: () => ({}),
});

const { attributeName, attributeValue } = useCatalogAttributes();
const { textListValue } = useCatalogTextList();
const fischer = useFischer();

const attributes = computed(() => {
  return props.catalogData?.Attributes;
});

const packageContentMedia = computed(() => {
  return props.catalogData?.Media?.filter((i) => i.Type === 'Package content') || [];
});

const hasPackageContentMedia = computed(() => {
  return fischer.app.isFite() && packageContentMedia.value.length > 0;
});

const textList = computed(() => {
  return props.catalogData?.TextList;
});

const hasPackageContent = computed(() => {
  return Boolean(textListValue(textList.value, 'PACKAGE_CONTENT') && fischer.app.isFite());
});

const hasContent = computed(() => {
  return (
    (attributes.value &&
      attributeName(attributes.value, 'INHALT') &&
      attributeValue(attributes.value, 'INHALT')) ||
    hasPackageContent.value ||
    hasPackageContentMedia.value
  );
});

const documents = computed(() => {
  if (props.catalogData?.Documents?.AdditionalDocuments) {
    return props.catalogData.Documents.AdditionalDocuments.filter(
      (el) => el.Type === 'Installation Instructions',
    );
  }

  return null;
});
</script>

<style src="./accordion-content-fi" />
